/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import { XProductModel } from "@eisinc/Overrides/Pages/ProductDetailsPage";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    customSettings: getSettingsCollection(state).customSettings,
});

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsPartNumberStyles {
    labelText?: TypographyProps;
    valueText?: TypographyProps;
    // xng-change
    container?: InjectableCss;
    skeletonWrapper?: InjectableCss;
}

export const partNumberStyles: ProductDetailsPartNumberStyles = {
    labelText: {
        size: 16,
        weight: "bold",
        css: css`
            margin-right: 15px;
        `,
    },
    valueText: {
        size: 16,
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = partNumberStyles;

const ProductDetailsPartNumber: React.FC<Props> = ({ product, customSettings }) => {
    const showModelNumber = (customSettings as any).showModelNumber.toLowerCase() === "true";
    const isLoading = (product as XProductModel)?.isLoading;
    if (isLoading) {
        return (
            <StyledWrapper {...styles.skeletonWrapper}>
                <Skeleton height="17px" width="100%" />
            </StyledWrapper>
        );
    }
    return (
        // xng-change: using ul and li for better semantic html
        <StyledWrapper as="ul" {...styles.container}>
            {product.customerProductNumber && (
                // xng-change
                <Typography {...styles.valueText} as="li" data-test-selector="customerProductNumber">
                    <Typography {...styles.labelText}>{siteMessage("Custom_Your_Part_#", "Your Part #")}</Typography>
                    {product.customerProductNumber}
                </Typography>
            )}
            {product.properties?.specNumber && (
                // xng-change
                <Typography {...styles.valueText} as="li">
                    <Typography {...styles.labelText}>{siteMessage("Custom_Spec_#", "Spec #")}</Typography>
                    {product.properties.specNumber}
                </Typography>
            )}
            {product.manufacturerItem && (
                // xng-change
                <Typography {...styles.valueText} as="li">
                    <Typography {...styles.labelText}>{siteMessage("Custom_MFR_#", "MFR #")}</Typography>
                    {product.manufacturerItem}
                </Typography>
            )}
            {product.productNumber && (
                // xng-change
                <Typography {...styles.valueText} as="li" data-test-selector="ProductDetailsPartNumber">
                    <Typography {...styles.labelText}>{siteMessage("Custom_Part_#", "EIS Part #")}</Typography>
                    {product.productNumber}
                </Typography>
            )}
            {product.detail?.productCode && (
                // xng-change
                <Typography {...styles.valueText} as="li">
                    <Typography {...styles.labelText}>{siteMessage("Custom_Series_#", "Series #")}</Typography>
                    {product.detail?.productCode}
                </Typography>
            )}
            {product.detail?.upcCode && (
                // xng-change
                <Typography {...styles.valueText} as="li">
                    <Typography {...styles.labelText}>{siteMessage("Custom_UPC_#", "UPC")}</Typography>
                    {product.detail.upcCode}
                </Typography>
            )}
            {product.detail?.modelNumber && showModelNumber && (
                // xng-change
                <Typography {...styles.valueText} as="li">
                    <Typography {...styles.labelText}>{siteMessage("Custom_Model_#", "Model #")}</Typography>
                    {product.detail.modelNumber}
                </Typography>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ProductDetailsPartNumber)),
    definition: {
        displayName: "Part Number",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
